import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  FileText, Search, Eye, Download, Trash2, RefreshCw, Filter,
  Car, User, Calendar, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAkten = () => {
  const navigate = useNavigate();
  const [akten, setAkten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('alle');

  const getToken = () => localStorage.getItem('autogutachter24_token');

  useEffect(() => {
    loadAkten();
  }, []);

  const loadAkten = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/akten/liste`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Fehler beim Laden');
      
      const data = await response.json();
      setAkten(data.akten || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Akten');
    } finally {
      setLoading(false);
    }
  };

  const deleteAkte = async (akteId) => {
    if (!confirm('Akte wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/akten/loeschen/${akteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Fehler beim Löschen');
      
      toast.success('Akte gelöscht');
      loadAkten();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const filteredAkten = akten.filter(akte => {
    const matchesSearch = 
      akte.aktenzeichen?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      akte.kunde?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      akte.kennzeichen?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      akte.marke?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filterStatus === 'alle') return matchesSearch;
    return matchesSearch && akte.status === filterStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'offen': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Offen' },
      'in_bearbeitung': { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Bearbeitung' },
      'abgeschlossen': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Abgeschlossen' }
    };
    const badge = badges[status] || badges['offen'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Alle Akten</h1>
            <p className="text-gray-500 mt-1">{akten.length} Akten insgesamt</p>
          </div>
          <Button onClick={loadAkten} variant="outline" className="border-gray-600 text-gray-600">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>

        {/* Filter & Suche */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2">
            <Search className="w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Akten suchen (AZ, Kunde, Kennzeichen)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-gray-800 w-80"
            />
          </div>
          
          <div className="flex space-x-2">
            {['alle', 'offen', 'in_bearbeitung', 'abgeschlossen'].map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                onClick={() => setFilterStatus(status)}
                className={filterStatus === status ? 'bg-orange-600' : 'border-gray-600 text-gray-600'}
                size="sm"
              >
                {status === 'alle' ? 'Alle' : status === 'offen' ? 'Offen' : status === 'in_bearbeitung' ? 'In Bearbeitung' : 'Abgeschlossen'}
              </Button>
            ))}
          </div>
        </div>

        {/* Akten-Tabelle */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 text-gray-500 animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Laden...</p>
              </div>
            ) : filteredAkten.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">Keine Akten gefunden</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Aktenzeichen</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Kunde</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Fahrzeug</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Schadentag</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Erstellt</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAkten.map((akte) => (
                    <tr key={akte.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-800">{akte.aktenzeichen}</span>
                        <p className="text-xs text-gray-500">{akte.typ || 'Gutachten'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-800">{akte.kunde || '-'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-800">{akte.marke} {akte.modell}</p>
                        <p className="text-xs text-gray-500 font-mono">{akte.kennzeichen}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(akte.schadentag)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(akte.erstellt_am)}</td>
                      <td className="px-6 py-4">{getStatusBadge(akte.status)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-gray-500 hover:text-gray-800"
                            onClick={() => window.open(`/neue-akte?id=${akte.id}`, '_blank')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400 hover:text-red-300"
                            onClick={() => deleteAkte(akte.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminAkten;
